import { Config } from 'types/Config';
import { getProcessEnv } from 'utils/fnEnvironments';
import { getLocalStorage, removeLocalStorage, setLocalStorage } from 'utils/fnStorage';

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export type ConfigCacheEnvelope = {
    data: Config;
    savedAt: number;
    appVersion: string;
    configEnv: string;
    tenant: string;
};

const getBuildIdentity = () => ({
    // eslint-disable-next-line no-underscore-dangle
    appVersion: window.__VERSION__ ?? '',
    configEnv: getProcessEnv('CONFIG_ENV', 'development') ?? '',
    tenant: getProcessEnv('TENANT', '') ?? '',
});

const hasUsableShape = (data: Config): boolean => Boolean(data?.app_config && data?.api_config && data?.feature_control);

const isCurrentBuild = (envelope: ConfigCacheEnvelope): boolean => {
    const identity = getBuildIdentity();

    return envelope.appVersion === identity.appVersion && envelope.configEnv === identity.configEnv && envelope.tenant === identity.tenant;
};

export const readConfigCache = (): Config | null => {
    const envelope = getLocalStorage('configCache');

    if (!envelope) {
        // also clears an unparseable value, which getLocalStorage reports the same as an absent one
        removeLocalStorage('configCache');
        return null;
    }

    if (typeof envelope.savedAt !== 'number' || !hasUsableShape(envelope.data)) {
        removeLocalStorage('configCache');
        return null;
    }

    // too old or from another build: refuse it, but leave it in place for now
    if (Date.now() - envelope.savedAt > MAX_AGE_MS || !isCurrentBuild(envelope)) {
        return null;
    }

    return envelope.data;
};

export const writeConfigCache = (data: Config) => {
    if (!hasUsableShape(data)) {
        return;
    }

    setLocalStorage('configCache', {
        data,
        savedAt: Date.now(),
        ...getBuildIdentity(),
    });

    removeLocalStorage('config');
};
